import { useQuery } from "@tanstack/react-query";
import { OutputDocumentConfirmDialog } from "components/OutputDocuments/OutputDocumentConfirmDialog";
import { createLogger } from "logging";
import { OutputDocumentViewModel } from "models/OutputDocumentViewModel";
import { useApi } from "providers";
import { useMeeting } from "providers";
import { useEdecidePanel } from "providers";
import { useStandardMeetingsPanel } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { OutputDocumentsPanel } from "./components/OutputDocumentsPanel";
import { OutputDocumentsPanelContext } from "./OutputDocumentsPanelContext";

const logger = createLogger("OutputDocumentsPanelProvider");

const displayConfirm = (document: OutputDocumentViewModel) => {
  return !!document.confirmTitle && !!document.confirmContent;
};

export const OutputDocumentsPanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { outputDocumentsApi } = useApi();
  const { reloadMeeting } = useMeeting();
  const { showStandardMeetingsPanel } = useStandardMeetingsPanel();
  const { openEdecidePanel } = useEdecidePanel();

  const [meetingId, setMeetingId] = useState<number | undefined>(undefined);
  const [agendaItemId, setAgendaItemId] = useState<number | undefined>(undefined);
  const [showOutputDocumentsPanel, setShowOutputDocumentsPanel] = useState(false);
  const [selectedOutputDocument, setSelectedOutputDocument] = useState<OutputDocumentViewModel | undefined>(undefined);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}|GenerateOutputDocument|${agendaItemId}|${selectedOutputDocument?.name}`],
    queryFn: async () => {
      const { name } = selectedOutputDocument!;
      if (!name) throw new Error("document.name is undefined!");
      logger.debug(`handleGenerate(): name:=${name}, meetingId:=${meetingId}, agendaItemId:=${agendaItemId}`);
      const data = outputDocumentsApi.generate(name, meetingId!, agendaItemId);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: showOutputDocumentsPanel && !!selectedOutputDocument && !!meetingId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });
  const model = queryResult.data;
  const isLoading = queryResult.isPending;

  useEffect(() => {
    if (model && selectedOutputDocument?.requiresModelRefresh) {
      reloadMeeting();
    }
  }, [model, selectedOutputDocument, reloadMeeting]);

  const hideConfirmDialog = useCallback(() => {
    setShowConfirmDialog(false);
    setSelectedOutputDocument(undefined);
  }, []);

  const onSelectDocument = useCallback(
    (document: OutputDocumentViewModel, selectedMeetingId: number, selectedAgendaItemId: number | undefined) => {
      const { name, url, target, disabled } = document;
      logger.debug("onSelectDocument(): ", document);

      if (disabled) {
        return;
      }

      if (name === "MeetingEdecide") {
        openEdecidePanel();
        return;
      }

      if (name === "ApplyStandardMeeting") {
        showStandardMeetingsPanel();
        return;
      }

      if (name === "MeetingSaveAsDefault") {
        setSelectedOutputDocument(document);
        return;
      }

      if (target && url) {
        window.open(url);
        return;
      }

      setMeetingId(selectedMeetingId);
      setAgendaItemId(selectedAgendaItemId);
      setSelectedOutputDocument(document);

      if (displayConfirm(document)) {
        setShowConfirmDialog(true);
      } else {
        setShowOutputDocumentsPanel(true);
      }
    },
    [openEdecidePanel, showStandardMeetingsPanel]
  );

  const contextValue = useMemo(
    () => ({
      showOutputDocumentsPanel,
      setShowOutputDocumentsPanel,
      selectedOutputDocument,
      setSelectedOutputDocument,
      showConfirmDialog,
      setShowConfirmDialog,
      onSelectDocument,
      hideConfirmDialog,
      isGenerating: isLoading,
      generatedDocuments: model?.documents,
    }),
    [
      showOutputDocumentsPanel,
      setShowOutputDocumentsPanel,
      selectedOutputDocument,
      setSelectedOutputDocument,
      showConfirmDialog,
      setShowConfirmDialog,
      onSelectDocument,
      hideConfirmDialog,
      isLoading,
      model,
    ]
  );

  return (
    <OutputDocumentsPanelContext.Provider value={contextValue}>
      {children}
      <OutputDocumentsPanel />
      <OutputDocumentConfirmDialog />
    </OutputDocumentsPanelContext.Provider>
  );
};
